import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { useToast } from '../components/ui/use-toast';
import { useBrand } from '../context/BrandContext';
import { Sparkles } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const FirstTimeSetup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refresh } = useBrand();
  const [formData, setFormData] = useState({
    brand_name: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    company: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand_name.trim()) {
      toast({ title: 'Missing field', description: 'Brand name is required', variant: 'destructive' });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    if (formData.password.length < 8) {
      toast({ title: 'Error', description: 'Password must be at least 8 characters', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/auth/setup`, {
        brand_name: formData.brand_name.trim(),
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        company: formData.company.trim() || formData.brand_name.trim()
      });

      toast({ title: 'Welcome!', description: `${formData.brand_name.trim()} is ready.` });
      
      localStorage.setItem('mcaToken', response.data.token);
      localStorage.setItem('mcaUser', JSON.stringify(response.data.user));
      
      // Refresh brand context so the rest of the app picks up the new name
      try { await refresh(); } catch {}
      
      // Hard reload so the auth context + brand context bootstrap cleanly
      window.location.href = '/funded-deals';
    } catch (error) {
      toast({ 
        title: 'Setup failed', 
        description: error.response?.data?.detail || 'Failed to create admin account', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center p-4" data-testid="first-time-setup">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-500 text-white rounded-full mb-3">
            <Sparkles size={22} />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-1">Welcome!</h1>
          <p className="text-gray-600 text-sm">Set up your MCA platform in under a minute.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Brand Name *</label>
            <Input
              required
              value={formData.brand_name}
              onChange={(e) => setFormData({...formData, brand_name: e.target.value})}
              placeholder="e.g. Acme Capital"
              disabled={loading}
              data-testid="setup-brand-name"
            />
            <p className="text-[11px] text-gray-500 mt-1">Shown on the login screen, sidebar, and browser tab.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Full Name *</label>
            <Input
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="John Doe"
              disabled={loading}
              data-testid="setup-admin-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Email Address *</label>
            <Input
              required
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="admin@company.com"
              disabled={loading}
              data-testid="setup-admin-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Company Name (optional)</label>
            <Input
              value={formData.company}
              onChange={(e) => setFormData({...formData, company: e.target.value})}
              placeholder="Defaults to Brand Name"
              disabled={loading}
              data-testid="setup-company"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Phone (optional, for SMS 2FA)</label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="+1 555 123 4567"
              disabled={loading}
              data-testid="setup-admin-phone"
            />
            <p className="text-[11px] text-gray-500 mt-1">2FA stays off until you configure Twilio in backend/.env.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Password *</label>
            <Input
              required
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              placeholder="Minimum 8 characters"
              disabled={loading}
              data-testid="setup-admin-password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Confirm Password *</label>
            <Input
              required
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              placeholder="Re-enter password"
              disabled={loading}
              data-testid="setup-admin-confirm"
            />
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 text-base font-semibold"
            data-testid="setup-submit"
          >
            {loading ? 'Setting Up\u2026' : 'Create Admin & Launch'}
          </Button>
        </form>

        <p className="mt-6 text-[11px] text-gray-500 text-center">
          One-time setup. After this, this screen is locked and you sign in via the login page.
        </p>
      </div>
    </div>
  );
};

export default FirstTimeSetup;
